import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Input, Modal, Pagination, Select, Space, Tabs, Table, Tag, Typography, Tooltip, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import request from '../../utils/request';
import { useTabs } from '../../contexts/TabsContext';

const { Title, Text } = Typography;

const DropshipBatchDetailPage: React.FC = () => {
  const params = useParams();
  const batchId = useMemo(() => Number(params.id), [params.id]);
  const tabsCtx: any = useTabs();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<any | null>(null);
  const [attPreviewOpen, setAttPreviewOpen] = useState(false);
  const [attPreviewLoading, setAttPreviewLoading] = useState(false);
  const [attPreviewTitle, setAttPreviewTitle] = useState('');
  const [attPreviewKind, setAttPreviewKind] = useState<'image' | 'pdf' | 'other'>('other');
  const [attPreviewUrl, setAttPreviewUrl] = useState('');
  const attPreviewObjectUrlRef = React.useRef<string | null>(null);
  const [itemsPage, setItemsPage] = useState(1);
  const [itemsPageSize, setItemsPageSize] = useState(20);
  const [inventoryPage, setInventoryPage] = useState(1);
  const [inventoryPageSize, setInventoryPageSize] = useState(20);
  const [logLoading, setLogLoading] = useState(false);
  const [logRows, setLogRows] = useState<any[]>([]);
  const [logPage, setLogPage] = useState(1);
  const [logPageSize, setLogPageSize] = useState(20);
  const [logTotal, setLogTotal] = useState(0);
  const [inventoryFilters, setInventoryFilters] = useState({
    customer: '',
    systemCode: '',
    customCode: '',
    sortingBarcode: '',
  });
  const resetInventoryFilters = React.useCallback(() => {
    setInventoryFilters({ customer: '', systemCode: '', customCode: '', sortingBarcode: '' });
  }, []);
  const [itemFilters, setItemFilters] = useState({
    customer: '',
    systemCode: '',
    batchItemStatus: '',
    unitKeyword: '',
    instructionType: '',
    waybillStatus: '',
    carrier: '',
    trackingNo: '',
    packRequired: '',
  });
  const resetItemFilters = React.useCallback(() => {
    setItemFilters({
      customer: '',
      systemCode: '',
      batchItemStatus: '',
      unitKeyword: '',
      instructionType: '',
      waybillStatus: '',
      carrier: '',
      trackingNo: '',
      packRequired: '',
    });
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!Number.isFinite(batchId) || !batchId) return;
      setLoading(true);
      try {
        const resp = await request.get(`/api/v1/instruction/batches/${batchId}/`);
        setDetail(resp?.data || null);
      } catch (e: any) {
        message.error(e?.message || '加载批次详情失败');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [batchId]);

  const loadLogs = React.useCallback(async () => {
    if (!Number.isFinite(batchId) || !batchId) return;
    setLogLoading(true);
    try {
      const resp = await request.get(`/api/v1/instruction/batches/${batchId}/logs/`, {
        params: { page: logPage, page_size: logPageSize },
      });
      const payload = resp?.data || {};
      const results = Array.isArray(payload?.results) ? payload.results : (Array.isArray(payload) ? payload : []);
      setLogRows(results);
      setLogTotal(Number(payload?.count ?? results.length ?? 0));
    } catch (e: any) {
      message.error(e?.message || '加载日志失败');
    } finally {
      setLogLoading(false);
    }
  }, [batchId, logPage, logPageSize]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    try {
      if (batchId) {
        if (tabsCtx && typeof tabsCtx.updateTabTitle === 'function') {
          tabsCtx.updateTabTitle(`批次详情-${batchId}`);
        }
        const w: any = window as any;
        if (w?.tabsManager && typeof w.tabsManager.updateTabTitle === 'function') {
          w.tabsManager.updateTabTitle(`批次详情-${batchId}`);
        }
      }
    } catch {}
  }, [batchId, tabsCtx]);
  useEffect(() => {
    try {
      if (detail?.system_code && batchId) {
        const basePath = String(window?.location?.pathname || '').split('?')[0];
        sessionStorage.setItem(`tab_title_batch:${basePath}`, String(detail.system_code));
      }
    } catch {}
  }, [detail?.system_code, batchId]);

  const items = useMemo(() => (Array.isArray(detail?.items) ? detail.items : []), [detail]);
  const inventoryDetail = useMemo(() => (Array.isArray(detail?.inventory_unit_detail) ? detail.inventory_unit_detail : []), [detail]);
  const inventorySummary = useMemo(() => {
    const raw = String(detail?.inventory_unit_summary || '').trim();
    if (!raw) return { kinds: 0, qty: 0 };
    const [k, q] = raw.split('|').map((v: string) => v.trim());
    const kindNum = Number(k || 0);
    const qtyNum = Number(q || 0);
    return {
      kinds: Number.isFinite(kindNum) ? kindNum : 0,
      qty: Number.isFinite(qtyNum) ? qtyNum : 0,
    };
  }, [detail]);

  const headerStats = useMemo(() => {
    const count = items.length || 0;
    const kinds = inventorySummary.kinds || 0;
    const qty = inventorySummary.qty || 0;
    return [
      `指令数量${count}`,
      `库存单元种类${kinds}`,
      `库存单元数量${qty}`,
    ];
  }, [items.length, inventorySummary.kinds, inventorySummary.qty]);
  const headerCreatedAt = useMemo(() => {
    return detail?.created_at ? dayjs(detail.created_at).format('YYYY-MM-DD HH:mm') : '-';
  }, [detail]);
  const batchStatusStyle = useMemo(() => {
    const status = String(detail?.status || '');
    const map: Record<string, { text: string; bg: string; border: string; color: string }> = {
      draft: { text: '草稿', bg: '#fafafa', border: '#d9d9d9', color: '#595959' },
      precheck_done: { text: '预审完成', bg: '#fffbe6', border: '#ffe58f', color: '#d48806' },
      executing: { text: '执行中', bg: '#e6f7ff', border: '#91d5ff', color: '#1890ff' },
      executed: { text: '执行完成', bg: '#f6ffed', border: '#b7eb8f', color: '#52c41a' },
      cancelled: { text: '已取消', bg: '#fff1f0', border: '#ffa39e', color: '#ff4d4f' },
    };
    return map[status] || { text: status || '-', bg: '#fafafa', border: '#d9d9d9', color: '#595959' };
  }, [detail?.status]);

  useEffect(() => {
    return () => {
      if (attPreviewObjectUrlRef.current) {
        try { URL.revokeObjectURL(attPreviewObjectUrlRef.current); } catch {}
        attPreviewObjectUrlRef.current = null;
      }
    };
  }, []);

  const resolveAttachmentUrl = useMemo(() => (att: any): string | undefined => {
    try {
      const raw = att?.file || att?.file_path || att?.file_name || '';
      if (!raw) return undefined;
      const isAbs = /^https?:\/\//i.test(raw);
      let backendOrigin = window.location.origin;
      try {
        const u = new URL(backendOrigin);
        if (u.port === '3000') { u.port = '8000'; backendOrigin = u.toString().replace(/\/$/, ''); }
      } catch {}
      try {
        const anyReq: any = request as any;
        const baseURL = anyReq?.defaults?.baseURL || anyReq?.getUri?.();
        if (baseURL && /^https?:\/\//i.test(baseURL)) {
          const b = new URL(baseURL);
          backendOrigin = `${b.protocol}//${b.host}`;
        }
      } catch {}
      let path = raw;
      if (!isAbs) {
        if (!path.startsWith('/')) path = '/' + path;
        if (/^\/(instruction|attachments?)/i.test(path)) path = '/media' + path; else if (!/^\/media\//i.test(path)) path = '/media' + path;
      }
      return isAbs ? raw : `${backendOrigin}${path}`;
    } catch {
      return undefined;
    }
  }, []);

  const getExtLower = useMemo(() => (att: any): string => {
    try {
      const raw = String(att?.file_name || att?.file || att?.file_path || '');
      const name = raw.split('?')[0].split('#')[0];
      const base = name.split('/').pop() || name;
      const ext = base.includes('.') ? base.split('.').pop() || '' : '';
      return String(ext).trim().toLowerCase();
    } catch {
      return '';
    }
  }, []);

  const getDisplayName = useMemo(() => (att: any): string => {
    return String(att?.file_name || att?.file || att?.file_path || att?.label || '').trim();
  }, []);

  const openAttachmentPreview = React.useCallback(async (att: any) => {
    const url = resolveAttachmentUrl(att);
    if (!url) { message.warning('无可用文件'); return; }
    const extLower = getExtLower(att);
    const isImg = ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extLower);
    const isPdf = extLower === 'pdf';
    const name = getDisplayName(att) || '附件预览';
    if (attPreviewObjectUrlRef.current) {
      try { URL.revokeObjectURL(attPreviewObjectUrlRef.current); } catch {}
      attPreviewObjectUrlRef.current = null;
    }
    if (!isImg && !isPdf) {
      window.open(url, '_blank');
      return;
    }
    setAttPreviewLoading(true);
    setAttPreviewTitle(name);
    setAttPreviewKind(isImg ? 'image' : 'pdf');
    setAttPreviewOpen(true);
    try {
      const resp = await fetch(url, { credentials: 'include' });
      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      attPreviewObjectUrlRef.current = objUrl;
      setAttPreviewUrl(objUrl);
    } catch (e: any) {
      message.error(e?.message || '预览失败');
      setAttPreviewOpen(false);
    } finally {
      setAttPreviewLoading(false);
    }
  }, [getDisplayName, getExtLower, resolveAttachmentUrl]);

  const formatShipUnits = useMemo(() => (row: any) => {
    try {
      const targets = Array.isArray(row?.targets_detail) ? row.targets_detail : [];
      const seen = new Set<string>();
      const lines: string[] = [];
      targets.forEach((t: any) => {
        const snap = t?.snapshot || {};
        const code = t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '';
        const customCodes = Array.isArray(t?.custom_codes) ? t.custom_codes : (Array.isArray(snap?.custom_codes) ? snap.custom_codes : []);
        const custom = customCodes.length > 0 ? customCodes.join(',') : (snap?.custom_code || snap?.customer_code || '');
        const sortArr = Array.isArray(t?.sorting_barcodes)
          ? t.sorting_barcodes.map((x: any) => x?.code ?? x).filter((x: any) => x && String(x).trim())
          : (Array.isArray(snap?.sorting_barcodes)
            ? snap.sorting_barcodes.map((x: any) => x?.code ?? x).filter((x: any) => x && String(x).trim())
            : []);
        const sort = sortArr.length > 0 ? sortArr.join(',') : (snap?.sorting_barcode || '');
        const qty = Number(t?.expected_qty || 0);
        const key = `${code}|${custom}|${sort}|${qty}`;
        if (!code || seen.has(key)) return;
        seen.add(key);
        const label = `${code}(${custom || '-'} | ${sort || '-'})×${Number.isFinite(qty) ? qty : 0}`;
        lines.push(label);
      });
      return lines;
    } catch {
      return [];
    }
  }, []);

  const getInstructionType = useMemo(() => (row: any) => {
    try {
      const targets = Array.isArray(row?.targets_detail) ? row.targets_detail : [];
      const byCode = new Map<string, number>();
      let totalQty = 0;
      targets.forEach((t: any) => {
        const snap = t?.snapshot || {};
        const code = String(t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '').trim();
        const qty = Number(t?.expected_qty || 0);
        if (!code) return;
        if (Number.isFinite(qty)) totalQty += qty;
        byCode.set(code, (byCode.get(code) || 0) + (Number.isFinite(qty) ? qty : 0));
      });
      const kindCount = byCode.size;
      if (kindCount <= 0) return '-';
      if (kindCount === 1) {
        return totalQty <= 1 ? '单款单件' : '单款多件';
      }
      return '多款多件';
    } catch {
      return '-';
    }
  }, []);
  const getWaybillStatus = useMemo(() => (row: any) => {
    try {
      const source = String(row?.waybill_source || '').trim().toLowerCase();
      const mode = String(row?.waybill_submit_mode || '').trim().toLowerCase();
      if (source === 'upload' && mode === 'immediate') return '有运单';
      if (source === 'warehouse') return '仓库提供运单';
      if (source === 'upload' && mode === 'after_pack') return '先申请包装数据';
      return '-';
    } catch {
      return '-';
    }
  }, []);
  const getInstructionStatus = useMemo(() => (row: any) => {
    const raw = String(row?.instruction_status || row?.status || '').trim().toLowerCase();
    const recheckRequired = !!row?.meta?.recheck_required;
    const map: Record<string, { text: string; color: string }> = {
      pending: { text: '待受理', color: 'default' },
      accepted: { text: '已受理', color: 'blue' },
      in_progress: { text: '执行中', color: 'processing' },
      operation_done: { text: '执行阶段完成(待终审)', color: 'gold' },
      completed: { text: '已完成', color: 'green' },
      rejected: { text: '已驳回', color: 'red' },
      cancelled: { text: '已取消', color: 'default' },
    };
    if (raw === 'rejected' && recheckRequired) {
      return <Tag color="orange">已驳回(待再次受理)</Tag>;
    }
    const it = map[raw];
    if (!it) return '-';
    return <Tag color={it.color}>{it.text}</Tag>;
  }, []);
  const getBatchItemStatus = useMemo(() => (row: any) => {
    const raw = String(row?.status || '').trim().toLowerCase();
    if (raw === 'done') return <Tag color="green">已完成</Tag>;
    if (raw === 'excluded') return <Tag color="red">已排除</Tag>;
    if (raw === 'pending') return <Tag color="default">待处理</Tag>;
    return '-';
  }, []);
  const getExcludedHint = useMemo(() => (row: any) => {
    const raw = String(row?.status || '').trim().toLowerCase();
    if (raw !== 'excluded') return '-';
    const reason = String(row?.error_reason || '').trim();
    const source = String(row?.meta?.excluded_source || '').trim().toLowerCase();
    const sourceText = source === 'precheck_reject'
      ? '预审排除'
      : source === 'dropship_pack_submit'
        ? '执行排除'
        : '';
    const text = [sourceText, reason].filter(Boolean).join('：');
    return text || '已排除';
  }, []);

  const displayDash = (v: any): string => {
    const s = String(v ?? '').trim();
    if (!s || s.toLowerCase() === 'none') return '-';
    return s;
  };
  const carrierPriority = useMemo(() => ['USPS', 'UPS', 'FEDEX'], []);
  const normalizeCarrier = useMemo(() => (v: any) => String(v || '').trim().toUpperCase(), []);
  const sortedItems = useMemo(() => {
    const list = Array.isArray(items) ? [...items] : [];
    return list.sort((a: any, b: any) => {
      const ca = normalizeCarrier(a?.waybill_carrier);
      const cb = normalizeCarrier(b?.waybill_carrier);
      const ia = carrierPriority.indexOf(ca);
      const ib = carrierPriority.indexOf(cb);
      const pa = ia === -1 ? 999 : ia;
      const pb = ib === -1 ? 999 : ib;
      if (pa !== pb) return pa - pb;
      if (ca && cb && ca !== cb) return ca.localeCompare(cb);
      if (!ca && cb) return 1;
      if (ca && !cb) return -1;
      return 0;
    });
  }, [items, carrierPriority, normalizeCarrier]);
  const carrierOptions = useMemo(() => {
    const set = new Set<string>();
    (items || []).forEach((r: any) => {
      const v = String(r?.waybill_carrier || '').trim();
      if (!v || v.toLowerCase() === 'none') return;
      set.add(v);
    });
    const list = Array.from(set);
    list.sort((a, b) => normalizeCarrier(a).localeCompare(normalizeCarrier(b)));
    return [{ label: '无承运商', value: '__NO_CARRIER__' }, ...list.map((v) => ({ label: v, value: v }))];
  }, [items, normalizeCarrier]);
  const itemCustomerOptions = useMemo(() => {
    const set = new Set<string>();
    (items || []).forEach((r: any) => {
      const v = String(
        r?.instruction_customer_username
        || r?.customer_username
        || (r?.instruction_customer_id ? `客户#${r.instruction_customer_id}` : '')
      ).trim();
      if (v) set.add(v);
    });
    const list = Array.from(set);
    list.sort((a, b) => String(a || '').toLowerCase().localeCompare(String(b || '').toLowerCase()));
    return list.map((v) => ({ label: v, value: v }));
  }, [items]);
  const carrierColors = useMemo(() => ([
    '#f5f7ff', '#f3fbff', '#f6ffed', '#fff7e6', '#fff0f6',
    '#f9f0ff', '#f6ffed', '#e6fffb', '#fffbe6', '#f0f5ff',
  ]), []);
  const getCarrierBg = useMemo(() => (carrier: any) => {
    const raw = String(carrier || '').trim();
    const lower = raw.toLowerCase();
    if (!raw || raw === '-' || lower === 'none' || lower === 'null' || lower === 'n/a') return '#f0f0f0';
    const v = String(carrier || 'UNKNOWN').trim().toUpperCase();
    let hash = 0;
    for (let i = 0; i < v.length; i += 1) {
      hash = ((hash << 5) - hash) + v.charCodeAt(i);
      hash |= 0;
    }
    const idx = Math.abs(hash) % carrierColors.length;
    return carrierColors[idx];
  }, [carrierColors]);
  const getUnitStats = useMemo(() => (row: any) => {
    try {
      const targets = Array.isArray(row?.targets_detail) ? row.targets_detail : [];
      const byCode = new Set<string>();
      let totalQty = 0;
      targets.forEach((t: any) => {
        const snap = t?.snapshot || {};
        const code = String(t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '').trim();
        const qty = Number(t?.expected_qty || 0);
        if (code) byCode.add(code);
        if (Number.isFinite(qty)) totalQty += qty;
      });
      return `${byCode.size || 0}款${totalQty || 0}件`;
    } catch {
      return '-';
    }
  }, []);
  const normalizeText = useMemo(() => (v: any) => String(v ?? '').trim().toLowerCase(), []);
  const getShipUnitSearchText = useMemo(() => (row: any) => {
    try {
      const targets = Array.isArray(row?.targets_detail) ? row.targets_detail : [];
      const parts: string[] = [];
      targets.forEach((t: any) => {
        const snap = t?.snapshot || {};
        const code = t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '';
        const customCodes = Array.isArray(t?.custom_codes) ? t.custom_codes : (Array.isArray(snap?.custom_codes) ? snap.custom_codes : []);
        const custom = customCodes.length > 0 ? customCodes.join(',') : (snap?.custom_code || snap?.customer_code || '');
        const sortArr = Array.isArray(t?.sorting_barcodes)
          ? t.sorting_barcodes.map((x: any) => x?.code ?? x).filter((x: any) => x && String(x).trim())
          : (Array.isArray(snap?.sorting_barcodes)
            ? snap.sorting_barcodes.map((x: any) => x?.code ?? x).filter((x: any) => x && String(x).trim())
            : []);
        const sort = sortArr.length > 0 ? sortArr.join(',') : (snap?.sorting_barcode || '');
        parts.push(String(code || ''), String(custom || ''), String(sort || ''));
      });
      return parts.filter(Boolean).join(' ');
    } catch {
      return '';
    }
  }, []);
  const filteredItems = useMemo(() => {
    const list = Array.isArray(sortedItems) ? sortedItems : [];
    const customerKey = normalizeText(itemFilters.customer);
    const sysKey = normalizeText(itemFilters.systemCode);
    const batchStatusKey = normalizeText(itemFilters.batchItemStatus);
    const unitKey = normalizeText(itemFilters.unitKeyword);
    const typeKey = normalizeText(itemFilters.instructionType);
    const waybillStatusKey = normalizeText(itemFilters.waybillStatus);
    const carrierKey = normalizeText(itemFilters.carrier);
    const trackKey = normalizeText(itemFilters.trackingNo);
    const packKey = normalizeText(itemFilters.packRequired);
    return list.filter((r: any) => {
      if (customerKey) {
        const c = normalizeText(
          r?.instruction_customer_username
          || r?.customer_username
          || (r?.instruction_customer_id ? `客户#${r.instruction_customer_id}` : '')
        );
        if (!c.includes(customerKey)) return false;
      }
      if (sysKey) {
        const code = normalizeText(r?.instruction_system_code);
        if (!code.includes(sysKey)) return false;
      }
      if (batchStatusKey) {
        const bs = normalizeText(r?.status);
        if (bs !== batchStatusKey) return false;
      }
      if (unitKey) {
        const text = normalizeText(getShipUnitSearchText(r));
        if (!text.includes(unitKey)) return false;
      }
      if (typeKey) {
        const t = normalizeText(getInstructionType(r));
        if (!t.includes(typeKey)) return false;
      }
      if (waybillStatusKey) {
        const ws = normalizeText(getWaybillStatus(r));
        if (ws !== waybillStatusKey) return false;
      }
      if (carrierKey) {
        const c = normalizeText(r?.waybill_carrier);
        if (carrierKey === '__no_carrier__') {
          if (c && c !== '-' && c !== 'none' && c !== 'null' && c !== 'n/a') return false;
        } else if (!c.includes(carrierKey)) {
          return false;
        }
      }
      if (trackKey) {
        const t = normalizeText(r?.waybill_tracking_no);
        if (!t.includes(trackKey)) return false;
      }
      if (packKey) {
        const flag = r?.is_pack_required === true ? '是' : (r?.is_pack_required === false ? '否' : '');
        const f = normalizeText(flag);
        if (!f.includes(packKey)) return false;
      }
      return true;
    });
  }, [
    sortedItems,
    itemFilters.customer,
    itemFilters.systemCode,
    itemFilters.batchItemStatus,
    itemFilters.unitKeyword,
    itemFilters.instructionType,
    itemFilters.waybillStatus,
    itemFilters.carrier,
    itemFilters.trackingNo,
    itemFilters.packRequired,
    normalizeText,
    getShipUnitSearchText,
    getInstructionType,
  ]);
  const pagedItems = useMemo(() => {
    const list = Array.isArray(filteredItems) ? filteredItems : [];
    const start = (itemsPage - 1) * itemsPageSize;
    return list.slice(start, start + itemsPageSize);
  }, [filteredItems, itemsPage, itemsPageSize]);
  useEffect(() => {
    setItemsPage(1);
  }, [
    itemFilters.customer,
    itemFilters.systemCode,
    itemFilters.batchItemStatus,
    itemFilters.unitKeyword,
    itemFilters.instructionType,
    itemFilters.waybillStatus,
    itemFilters.carrier,
    itemFilters.trackingNo,
    itemFilters.packRequired,
  ]);

  const instructionColumns = useMemo(() => ([
    {
      title: '所属客户',
      key: 'instruction_customer_username',
      width: 150,
      render: (_: any, r: any) => displayDash(
        r?.instruction_customer_username
        || r?.customer_username
        || (r?.instruction_customer_id ? `客户#${r.instruction_customer_id}` : '')
      ),
    },
    {
      title: '指令系统编码',
      dataIndex: 'instruction_system_code',
      key: 'instruction_system_code',
      width: 120,
      render: (v: any, r: any) => {
        const code = displayDash(v);
        if (code === '-') return '-';
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Button
              type="link"
              style={{ padding: 0, height: 'auto' }}
              onClick={() => {
                try {
                  const path = String((window as any)?.location?.pathname || '');
                  const base = path.startsWith('/dashboard/staff')
                    ? '/dashboard/staff'
                    : path.startsWith('/dashboard/admin') ? '/dashboard/admin' : '/dashboard/customer';
                  navigate(`${base}/instructions/${(r as any)?.instruction}`);
                } catch {}
              }}
            >
              {code}
            </Button>
            <Tooltip title="复制">
              <CopyOutlined
                onClick={async (e) => {
                  e?.stopPropagation?.();
                  try {
                    await navigator.clipboard.writeText(String(code));
                    message.success('已复制');
                  } catch {
                    message.error('复制失败');
                  }
                }}
              />
            </Tooltip>
          </span>
        );
      },
    },
    {
      title: '指令当前状态',
      key: 'instruction_status',
      width: 150,
      render: (_: any, r: any) => getInstructionStatus(r),
    },
    {
      title: '批次内状态',
      key: 'batch_item_status',
      width: 120,
      render: (_: any, r: any) => getBatchItemStatus(r),
    },
    {
      title: '排除原因',
      key: 'excluded_hint',
      width: 100,
      render: (_: any, r: any) => {
        const text = getExcludedHint(r);
        if (text === '-') return '-';
        return (
          <Tooltip title={text}>
            <span
              style={{
                display: 'inline-block',
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: '#cf1322',
              }}
            >
              {text}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: '发货库存单元',
      key: 'ship_units',
      width: 200,
      render: (_: any, r: any) => {
        const lines = formatShipUnits(r);
        if (!lines.length) return '-';
        return (
          <div>
            {lines.map((line, idx) => (
              <Tooltip key={`${line}-${idx}`} title={line}>
                <div
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    wordBreak: 'break-all',
                  }}
                >
                  {line}
                </div>
              </Tooltip>
            ))}
          </div>
        );
      },
    },
    {
      title: '统计',
      key: 'unit_stats',
      width: 150,
      render: (_: any, r: any) => getUnitStats(r),
    },
    {
      title: '指令类型',
      key: 'instruction_type',
      width: 140,
      render: (_: any, r: any) => getInstructionType(r),
    },
    {
      title: '运单状态',
      key: 'waybill_status',
      width: 170,
      render: (_: any, r: any) => getWaybillStatus(r),
    },
    {
      title: '承运商',
      key: 'waybill_carrier',
      width: 90,
      render: (_: any, r: any) => displayDash(r?.waybill_carrier),
    },
    {
      title: '物流跟踪号',
      key: 'waybill_tracking_no',
      width: 140,
      render: (_: any, r: any) => {
        const text = displayDash(r?.waybill_tracking_no);
        if (text === '-') return '-';
        return (
          <Tooltip title={text}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  display: 'inline-block',
                  maxWidth: 90,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  verticalAlign: 'bottom',
                }}
              >
                {text}
              </span>
              <CopyOutlined
                onClick={async (e) => {
                  e?.stopPropagation?.();
                  try {
                    await navigator.clipboard.writeText(String(text));
                    message.success('已复制');
                  } catch {
                    message.error('复制失败');
                  }
                }}
              />
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: '运单附件',
      key: 'waybill_attachments',
      width: 90,
      render: (_: any, r: any) => {
        const list = Array.isArray(r?.attachments_detail) ? r.attachments_detail : [];
        const filtered = list.filter((a: any) => {
          const label = String(a?.label || '').toLowerCase();
          return label.includes('运单') || label.includes('waybill') || label.includes('tracking');
        });
        const useList = filtered.length ? filtered : list;
        if (!useList.length) return '-';
        return (
          <div>
            {useList.map((a: any, idx: number) => {
              const name = displayDash(a?.file_name || a?.file || a?.file_path || a?.label || '');
              if (name === '-') return null;
              return (
                <Tooltip key={`${name}-${idx}`} title={name}>
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: 'auto' }}
                    onClick={() => openAttachmentPreview(a)}
                  >
                    {name}
                  </Button>
                </Tooltip>
              );
            })}
          </div>
        );
      },
    },
    {
      title: '是否需打包',
      key: 'is_pack_required',
      width: 90,
      render: (_: any, r: any) => {
        if (r?.is_pack_required === true) return <Tag color="red">是</Tag>;
        if (r?.is_pack_required === false) return <Tag color="green">否</Tag>;
        return '-';
      },
    },
    {
      title: '创建时间',
      key: 'created_at',
      width: 130,
      render: (_: any, r: any) => (r?.created_at ? dayjs(r.created_at).format('YYYY-MM-DD HH:mm') : '-'),
    },
  ]), [displayDash, formatShipUnits, getBatchItemStatus, getExcludedHint, getInstructionStatus, getInstructionType, getUnitStats, navigate, openAttachmentPreview]);

  const inventorySystemCodeOptions = useMemo(() => {
    const set = new Set<string>();
    (inventoryDetail || []).forEach((r: any) => {
      const v = String(r?.inventory_unit_code || '').trim();
      if (!v || v.toLowerCase() === 'none') return;
      set.add(v);
    });
    const list = Array.from(set);
    list.sort((a, b) => normalizeText(a).localeCompare(normalizeText(b)));
    return list.map((v) => ({ label: v, value: v }));
  }, [inventoryDetail, normalizeText]);
  const inventoryCustomerOptions = useMemo(() => {
    const set = new Set<string>();
    (inventoryDetail || []).forEach((r: any) => {
      const raw = String(r?.customer_usernames || '').trim();
      if (!raw) return;
      raw
        .split(',')
        .map((x) => String(x || '').trim())
        .filter(Boolean)
        .forEach((name) => set.add(name));
    });
    const list = Array.from(set);
    list.sort((a, b) => String(a || '').toLowerCase().localeCompare(String(b || '').toLowerCase()));
    return list.map((v) => ({ label: v, value: v }));
  }, [inventoryDetail]);
  const inventoryColumns = useMemo(() => ([
    {
      title: '所属客户',
      dataIndex: 'customer_usernames',
      key: 'customer_usernames',
      width: 140,
      render: (v: any) => displayDash(v),
    },
    {
      title: '库存单元系统编码',
      dataIndex: 'inventory_unit_code',
      key: 'inventory_unit_code',
      width: 160,
      render: (v: any) => {
        const code = String(v || '').trim();
        if (!code) return '-';
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span>{code}</span>
            <Tooltip title="复制">
              <CopyOutlined
                onClick={async (e) => {
                  e?.stopPropagation?.();
                  try {
                    await navigator.clipboard.writeText(String(code));
                    message.success('已复制');
                  } catch {
                    message.error('复制失败');
                  }
                }}
              />
            </Tooltip>
          </span>
        );
      },
    },
    {
      title: '客户自定义编码',
      dataIndex: 'custom_codes',
      key: 'custom_codes',
      width: 160,
      render: (v: any) => v || '-',
    },
    {
      title: '分拣条码',
      dataIndex: 'sorting_barcodes',
      key: 'sorting_barcodes',
      width: 160,
      render: (v: any) => {
        const text = String(v || '').trim();
        if (!text) return '-';
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span>{text}</span>
            <Tooltip title="复制">
              <CopyOutlined
                onClick={async (e) => {
                  e?.stopPropagation?.();
                  try {
                    await navigator.clipboard.writeText(String(text));
                    message.success('已复制');
                  } catch {
                    message.error('复制失败');
                  }
                }}
              />
            </Tooltip>
          </span>
        );
      },
    },
    {
      title: '创建批次前可用库存数量',
      dataIndex: 'available_qty',
      key: 'available_qty',
      width: 120,
      render: (v: any) => (Number(v) || 0),
    },
    {
      title: '发货总数量',
      dataIndex: 'ship_qty',
      key: 'ship_qty',
      width: 120,
      render: (v: any) => v || '0',
    },
    {
      title: '库存是否充足',
      dataIndex: 'is_sufficient',
      key: 'is_sufficient',
      width: 120,
      render: (v: any) => (
        <Tag color={v ? 'green' : 'red'}>{v ? '充足' : '不足'}</Tag>
      ),
    },
  ]), []);
  const sortedInventoryDetail = useMemo(() => {
    const list = Array.isArray(inventoryDetail) ? [...inventoryDetail] : [];
    return list.sort((a: any, b: any) => {
      const qa = Number(a?.ship_qty || 0);
      const qb = Number(b?.ship_qty || 0);
      if (Number.isFinite(qb) && Number.isFinite(qa)) return qb - qa;
      return 0;
    });
  }, [inventoryDetail]);
  const filteredInventoryDetail = useMemo(() => {
    const list = Array.isArray(sortedInventoryDetail) ? sortedInventoryDetail : [];
    const customerKey = normalizeText(inventoryFilters.customer);
    const sysKey = normalizeText(inventoryFilters.systemCode);
    const customKey = normalizeText(inventoryFilters.customCode);
    const sortKey = normalizeText(inventoryFilters.sortingBarcode);
    return list.filter((r: any) => {
      if (customerKey) {
        const c = normalizeText(r?.customer_usernames);
        if (!c.includes(customerKey)) return false;
      }
      if (sysKey) {
        const c = normalizeText(r?.inventory_unit_code);
        if (!c.includes(sysKey)) return false;
      }
      if (customKey) {
        const c = normalizeText(r?.custom_codes);
        if (!c.includes(customKey)) return false;
      }
      if (sortKey) {
        const s = normalizeText(r?.sorting_barcodes);
        if (!s.includes(sortKey)) return false;
      }
      return true;
    });
  }, [
    sortedInventoryDetail,
    inventoryFilters.customer,
    inventoryFilters.systemCode,
    inventoryFilters.customCode,
    inventoryFilters.sortingBarcode,
    normalizeText,
  ]);
  const pagedFilteredInventoryDetail = useMemo(() => {
    const list = Array.isArray(filteredInventoryDetail) ? filteredInventoryDetail : [];
    const start = (inventoryPage - 1) * inventoryPageSize;
    return list.slice(start, start + inventoryPageSize);
  }, [filteredInventoryDetail, inventoryPage, inventoryPageSize]);
  useEffect(() => {
    setInventoryPage(1);
  }, [inventoryPageSize, filteredInventoryDetail.length]);
  useEffect(() => {
    setInventoryPage(1);
  }, [
    inventoryFilters.customer,
    inventoryFilters.systemCode,
    inventoryFilters.customCode,
    inventoryFilters.sortingBarcode,
  ]);

  return (
    <div className="list-page-container">
      <Card loading={loading}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            <Title level={4} style={{ margin: 0 }}>批次详情(一件代发)</Title>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 50 }}>
              <Text style={{ color: '#1890ff', fontSize: 20, fontWeight: 500 }}>{detail?.system_code || '-'}</Text>
              <Button
                size="small"
                style={{ background: batchStatusStyle.bg, borderColor: batchStatusStyle.border, color: batchStatusStyle.color }}
              >
                {batchStatusStyle.text}
              </Button>
              <div style={{ display: 'flex', alignItems: 'center', marginLeft: 50, gap: 16, color: '#8c8c8c', fontSize: 14 }}>
                {headerStats.map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </div>
          </div>
          <Tooltip title={headerCreatedAt}>
            <Text type="secondary">{`创建时间${headerCreatedAt}`}</Text>
          </Tooltip>
        </div>

        <Tabs
          items={[
            {
              key: 'items',
              label: '指令列表',
              children: (
                <div>
                  <div style={{ marginBottom: 12 }}>
                    <Space size={10} wrap>
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="所属客户"
                        value={itemFilters.customer || undefined}
                        onChange={(v) => setItemFilters((prev) => ({ ...prev, customer: String(v || '') }))}
                        style={{ width: 160 }}
                        options={itemCustomerOptions}
                      />
                      <Input
                        allowClear
                        placeholder="指令系统编码"
                        value={itemFilters.systemCode}
                        onChange={(e) => setItemFilters((prev) => ({ ...prev, systemCode: e.target.value }))}
                        style={{ width: 180 }}
                      />
                      <Select
                        allowClear
                        placeholder="批次内状态"
                        value={itemFilters.batchItemStatus || undefined}
                        onChange={(v) => setItemFilters((prev) => ({ ...prev, batchItemStatus: String(v || '') }))}
                        style={{ width: 140 }}
                        options={[
                          { label: '待处理', value: 'pending' },
                          { label: '已完成', value: 'done' },
                          { label: '已排除', value: 'excluded' },
                        ]}
                      />
                      <Input
                        allowClear
                        placeholder="库存单元信息"
                        value={itemFilters.unitKeyword}
                        onChange={(e) => setItemFilters((prev) => ({ ...prev, unitKeyword: e.target.value }))}
                        style={{ width: 200 }}
                      />
                      <Select
                        allowClear
                        placeholder="指令类型"
                        value={itemFilters.instructionType || undefined}
                        onChange={(v) => setItemFilters((prev) => ({ ...prev, instructionType: String(v || '') }))}
                        style={{ width: 160 }}
                        options={[
                          { label: '单款单件', value: '单款单件' },
                          { label: '单款多件', value: '单款多件' },
                          { label: '多款多件', value: '多款多件' },
                        ]}
                      />
                      <Select
                        allowClear
                        placeholder="运单状态"
                        value={itemFilters.waybillStatus || undefined}
                        onChange={(v) => setItemFilters((prev) => ({ ...prev, waybillStatus: String(v || '') }))}
                        style={{ width: 170 }}
                        options={[
                          { label: '有运单', value: '有运单' },
                          { label: '仓库提供运单', value: '仓库提供运单' },
                          { label: '先申请包装数据', value: '先申请包装数据' },
                        ]}
                      />
                      <Select
                        allowClear
                        placeholder="承运商"
                        value={itemFilters.carrier || undefined}
                        onChange={(v) => setItemFilters((prev) => ({ ...prev, carrier: String(v || '') }))}
                        style={{ width: 160 }}
                        options={carrierOptions}
                        showSearch
                        optionFilterProp="label"
                      />
                      <Input
                        allowClear
                        placeholder="物流跟踪号"
                        value={itemFilters.trackingNo}
                        onChange={(e) => setItemFilters((prev) => ({ ...prev, trackingNo: e.target.value }))}
                        style={{ width: 160 }}
                      />
                      <Select
                        allowClear
                        placeholder="是否打包"
                        value={itemFilters.packRequired || undefined}
                        onChange={(v) => setItemFilters((prev) => ({ ...prev, packRequired: String(v || '') }))}
                        style={{ width: 120 }}
                        options={[
                          { label: '是', value: '是' },
                          { label: '否', value: '否' },
                        ]}
                      />
                      <Button onClick={resetItemFilters}>重置</Button>
                      <span style={{ color: '#666' }}>
                        {`当前结果：${filteredItems.length}条`}
                      </span>
                    </Space>
                  </div>
                  <div>
                    <Table
                      rowKey={(r: any) => String(r?.instruction || r?.id || '')}
                      dataSource={pagedItems}
                      columns={instructionColumns as any}
                      scroll={{ x: 2500 }}
                      pagination={false}
                      onRow={(record) => ({
                        style: { background: getCarrierBg(record?.waybill_carrier) },
                      })}
                    />
                    <div className="flex justify-end items-center" style={{ marginTop: 12, gap: 12 }}>
                      <span className="text-gray-600">
                        {(() => {
                          const total = filteredItems.length;
                          const start = total > 0 ? ((itemsPage - 1) * itemsPageSize) + 1 : 0;
                          const end = total > 0 ? start + Math.max(pagedItems.length - 1, 0) : 0;
                          return `第 ${start} - ${end} 条，共 ${total} 条记录`;
                        })()}
                      </span>
                      <Pagination
                        current={itemsPage}
                        pageSize={itemsPageSize}
                        total={filteredItems.length}
                        showSizeChanger
                        showQuickJumper
                        pageSizeOptions={[10, 20, 50, 100] as any}
                        onChange={(page, pageSize) => {
                          setItemsPage(page);
                          if (pageSize && pageSize !== itemsPageSize) {
                            setItemsPageSize(pageSize);
                            setItemsPage(1);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              ),
            },
            {
              key: 'inventory',
              label: '发货库存单元信息',
              children: (
                <div>
                  <div style={{ marginBottom: 12 }}>
                    <Space size={10} wrap>
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="所属客户"
                        value={inventoryFilters.customer || undefined}
                        onChange={(v) => setInventoryFilters((prev) => ({ ...prev, customer: String(v || '') }))}
                        style={{ width: 160 }}
                        options={inventoryCustomerOptions}
                      />
                      <Select
                        allowClear
                        placeholder="库存单元系统编码"
                        value={inventoryFilters.systemCode || undefined}
                        onChange={(v) => setInventoryFilters((prev) => ({ ...prev, systemCode: String(v || '') }))}
                        style={{ width: 200 }}
                        options={inventorySystemCodeOptions}
                        showSearch
                        optionFilterProp="label"
                      />
                      <Input
                        allowClear
                        placeholder="客户自定义编码"
                        value={inventoryFilters.customCode}
                        onChange={(e) => setInventoryFilters((prev) => ({ ...prev, customCode: e.target.value }))}
                        style={{ width: 200 }}
                      />
                      <Input
                        allowClear
                        placeholder="分拣条码"
                        value={inventoryFilters.sortingBarcode}
                        onChange={(e) => setInventoryFilters((prev) => ({ ...prev, sortingBarcode: e.target.value }))}
                        style={{ width: 180 }}
                      />
                      <Button onClick={resetInventoryFilters}>重置</Button>
                      <span style={{ color: '#666' }}>
                        {`当前结果：${filteredInventoryDetail.length}条`}
                      </span>
                    </Space>
                  </div>
                  <Table
                    rowKey={(r: any) => String(r?.inventory_unit_id || r?.inventory_unit_code || '')}
                    dataSource={pagedFilteredInventoryDetail}
                    columns={inventoryColumns as any}
                    pagination={false}
                  />
                  <div className="flex justify-end items-center" style={{ marginTop: 12, gap: 12 }}>
                    <span className="text-gray-600">
                      {(() => {
                        const total = filteredInventoryDetail.length;
                        const start = total > 0 ? ((inventoryPage - 1) * inventoryPageSize) + 1 : 0;
                        const end = total > 0 ? start + Math.max(pagedFilteredInventoryDetail.length - 1, 0) : 0;
                        return `第 ${start} - ${end} 条，共 ${total} 条记录`;
                      })()}
                    </span>
                    <Pagination
                      current={inventoryPage}
                      pageSize={inventoryPageSize}
                      total={filteredInventoryDetail.length}
                      showSizeChanger
                      showQuickJumper
                      pageSizeOptions={[10, 20, 50, 100] as any}
                      onChange={(page, pageSize) => {
                        setInventoryPage(page);
                        if (pageSize && pageSize !== inventoryPageSize) {
                          setInventoryPageSize(pageSize);
                          setInventoryPage(1);
                        }
                      }}
                    />
                  </div>
                </div>
              ),
            },
            {
              key: 'logs',
              label: '日志',
              children: (
                <div>
                  <Table
                    rowKey={(r: any) => String(r?.id ?? '')}
                    dataSource={logRows}
                    loading={logLoading}
                    pagination={false}
                    columns={[
                      {
                        title: '时间',
                        dataIndex: 'created_at',
                        key: 'created_at',
                        width: 180,
                        render: (v: any) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'),
                      },
                      {
                        title: '事件',
                        dataIndex: 'event',
                        key: 'event',
                        width: 140,
                        render: (v: any) => {
                          const map: Record<string, string> = {
                            created: '创建批次',
                            precheck_done: '预审完成',
                            executing: '开始执行',
                            executed: '执行完成',
                            cancelled: '取消',
                          };
                          return map[String(v || '')] || String(v || '-') || '-';
                        },
                      },
                      {
                        title: '操作人',
                        dataIndex: 'operator_name',
                        key: 'operator_name',
                        width: 160,
                        render: (v: any) => v || '-',
                      },
                      {
                        title: '内容',
                        dataIndex: 'message',
                        key: 'message',
                        render: (v: any) => v || '-',
                      },
                    ]}
                  />
                  <div className="flex justify-end items-center" style={{ marginTop: 12, gap: 12 }}>
                    <span className="text-gray-600">
                      {(() => {
                        const total = logTotal;
                        const start = total > 0 ? ((logPage - 1) * logPageSize) + 1 : 0;
                        const end = total > 0 ? start + Math.max(logRows.length - 1, 0) : 0;
                        return `第 ${start} - ${end} 条，共 ${total} 条记录`;
                      })()}
                    </span>
                    <Pagination
                      current={logPage}
                      pageSize={logPageSize}
                      total={logTotal}
                      showSizeChanger
                      showQuickJumper
                      pageSizeOptions={[10, 20, 50, 100] as any}
                      onChange={(page, pageSize) => {
                        setLogPage(page);
                        if (pageSize && pageSize !== logPageSize) {
                          setLogPageSize(pageSize);
                          setLogPage(1);
                        }
                      }}
                    />
                  </div>
                </div>
              ),
            },
          ]}
        />
        <Modal
          open={attPreviewOpen}
          title={attPreviewTitle || '附件预览'}
          footer={null}
          width={900}
          onCancel={() => {
            setAttPreviewOpen(false);
            setAttPreviewUrl('');
            if (attPreviewObjectUrlRef.current) {
              try { URL.revokeObjectURL(attPreviewObjectUrlRef.current); } catch {}
              attPreviewObjectUrlRef.current = null;
            }
          }}
        >
          {attPreviewLoading ? (
            <div style={{ padding: 24 }}>加载中...</div>
          ) : attPreviewKind === 'image' ? (
            <img src={attPreviewUrl} alt="preview" style={{ maxWidth: '100%' }} />
          ) : (
            <iframe title="preview" src={attPreviewUrl} style={{ width: '100%', height: 600, border: 'none' }} />
          )}
        </Modal>
      </Card>
    </div>
  );
};

export default DropshipBatchDetailPage;
